import type { AtlasNodeMolecule } from 'types/contextView';

export interface AtlasEnrichmentOverrides {
  includeStatus: boolean;
  includeFlow: boolean;
  includeEdgeCount: boolean;
  includePerspectives: boolean;
}

/**
 * Derives enrichment flags from the node-molecule composition in a ContextView.
 * Replaces the inline useMemo in AtlasTree / the static ATLAS_ENRICHMENT in AtlasTreeMobile.
 */
export function deriveEnrichmentFromMolecules(
  nodeMolecules: AtlasNodeMolecule[]
): AtlasEnrichmentOverrides {
  const types = new Set(nodeMolecules.map((m) => m.type));
  return {
    includeStatus: true,
    // 'FlowStageSelector' is the legacy alias for 'ArcStageSelector' (Flow→Arc rename).
    includeFlow: types.has('ArcStageSelector') || types.has('FlowStageSelector'),
    includeEdgeCount: types.has('NodeCounts'),
    includePerspectives: types.has('PerspectiveSelector'),
  };
}

/**
 * Collects the custom sphere-property names that node molecules render as
 * columns (e.g. an 'Aspect' column bound to 'startDate', 'targetDate' or
 * 'timeSpent'). These are NOT in the execute query's base vertex projection,
 * so they must be declared to the backend via `columnProperties` or the
 * corresponding columns render empty.
 *
 * Before EAINT-866 this switched on 'AtlasDateSelector'/'AtlasTextColumn';
 * both collapsed onto 'Aspect' (`moleculeProps.aspect`), which is now the
 * only branch that needs a declared property — the legacy branches are
 * unreachable post-migration and were removed rather than left dead.
 */
export function deriveColumnPropertiesFromMolecules(
  nodeMolecules: AtlasNodeMolecule[]
): string[] {
  const props = new Set<string>();
  for (const molecule of nodeMolecules) {
    const moleculeProps = molecule.moleculeProps ?? {};
    if (molecule.type === 'Aspect') {
      const aspect = moleculeProps.aspect;
      if (typeof aspect === 'string' && aspect) props.add(aspect);
    }
  }
  return [...props];
}
